import { Component } from "react"
import { v4 as uuid } from "uuid"
import PropTypes from "prop-types"
import classNames from "classnames"
import { connect } from "react-redux"
import { find, get, map } from "lodash"
import moment from "moment"
import resizable from "hocs/resizable"
import tooltipContainer from "hocs/tooltip-container"
import Tooltip from "components/charts/tooltip"
import { HIGHLIGHTER } from "../shapes"

import ChartDefs from "../multi-line-breakdown/components/chart-defs"
import LegendItem from "../multi-line-breakdown/legend-item"
import Axis from "../multi-line-breakdown/axis"
import Dots from "../multi-line-breakdown/dots"
import "./style.scss"

import { getTotalLine, getTotalYAxis, getFindTotalByOffset } from "./selectors/total"
import { getBreakdownAreas, getBreakdownYAxis, getBreakdownScaleY, getFindBreakdownByOffset } from "./selectors/breakdown"
import { getXAxis, getScaleX, getTotalScaleY } from "./selectors"
import { getBreakdownItems, getTotalItem, getOnlyLegendText } from "./selectors/items"
import { getHighlighter } from "./selectors/highlighter"

const AXIS = PropTypes.shape({
  fn: PropTypes.func.isRequired,
  transform: PropTypes.string.isRequired,
  baseline: PropTypes.shape({
    x1: PropTypes.number.isRequired,
    x2: PropTypes.number.isRequired,
    y1: PropTypes.number.isRequired,
    y2: PropTypes.number.isRequired,
  }).isRequired,
})

class MultiLineBreakdownChart extends Component {
  static propTypes = {
    totalLine: PropTypes.object,
    data: PropTypes.array.isRequired,
    totalItem: PropTypes.array.isRequired,
    breakdownAreas: PropTypes.array.isRequired,
    highlighter: HIGHLIGHTER,
    width: PropTypes.number,
    resizableRef: PropTypes.func.isRequired,
    xAxis: AXIS.isRequired,
    yAxis: AXIS.isRequired,
    yBreakdownAxis: AXIS.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })).isRequired,
    tooltip: PropTypes.element.isRequired,
    scaleX: PropTypes.func.isRequired,
    totalScaleY: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    setDate: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    findBreakdownByOffset: PropTypes.func.isRequired,
    findTotalByOffset: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    totalLabel: PropTypes.string,
    breakdownLabel: PropTypes.string,
    toggleOnly: PropTypes.func.isRequired,
    onlyLegendText: PropTypes.string.isRequired,
  }

  constructor(props) {
    super(props)

    const chartId = uuid()
    this.pathGradientId = `path-gradient-${chartId}`
    this.axisGradientId = `axis-gradient-${chartId}`
    this.areaGradientId = `area-gradient-${chartId}`
  }

  legendRef = null

  setDate = offset => {
    const { setDate } = this.props
    const datum = this.findByOffset(offset)
    if (datum) setDate(moment(datum.date).format("YYYY-MM-DD"))
  }

  findByOffset = offset => {
    const { data, scaleX } = this.props
    const lookup = scaleX.invert(offset - 18) // -2 is to give a margin of selection on the first element, -16 for the margin
    return find(data, datum => datum.date >= lookup)
  }

  handleGraphClick = ev => {
    const box = ev.currentTarget.getBoundingClientRect()
    const offsetX = ev.pageX - box.left + 16
    this.setDate(offsetX)
  }

  computeAxisStyle = () => {
    const { highlighter, width } = this.props
    const axisStyle = {}

    if (highlighter && width) {
      axisStyle.stroke = `url(#${this.axisGradientId})`
    }

    return axisStyle
  }

  findTotalDots = offset => {
    const { scaleX, totalScaleY, totalItem, findTotalByOffset } = this.props
    const datum = findTotalByOffset(offset)

    if (datum) {
      return map(totalItem, item => ({
        id: item.id,
        color: item.color,
        y: totalScaleY(get(datum, item.id, 0)),
        x: scaleX(datum.date),
      }))
    }
  }

  findBreakdownDots = offset => {
    const { findBreakdownByOffset } = this.props
    const datum = findBreakdownByOffset(offset)

    if (datum.length) return datum
  }

  computeStyle = (id, initialStyle, property) => {
    const { highlighter, width } = this.props
    const pathStyle = {}

    if (highlighter && width) {
      pathStyle[property] = `url(#${id})`
    } else {
      pathStyle[property] = initialStyle
    }

    return pathStyle
  }

  registerLegendRef = element => { this.legendRef = element }

  render() {
    const {
      breakdownAreas,
      resizableRef,
      xAxis,
      yAxis,
      yBreakdownAxis,
      items,
      totalLine,
      toggle,
      focusedChartLine,
      focusChartLine,
      blurChartLine,
      subscribe,
      tooltip,
      highlighter,
      unsubscribe,
      width,
      totalLabel,
      breakdownLabel,
      data,
      toggleOnly,
      onlyLegendText,
    } = this.props
    if (!data.length) {
      return <div ref={resizableRef} className="components_charts_multi-line-overlapping no-data">No significant data was found. Adjust the date range to a larger time frame to view graph.</div>
    } else {
      return <div className="components_charts_multi-line-overlapping">
        <div className="legend" ref={this.registerLegendRef}>
          {map(items, item =>
            <LegendItem
              onlyLegendText={onlyLegendText}
              key={item.id}
              {...item}
              onClick={toggle}
              onOnlyToggle={toggleOnly}
              focus={focusChartLine}
              blur={blurChartLine}
              focused={focusedChartLine}
            />,
          )}
        </div>
        <svg ref={resizableRef} onClick={this.handleGraphClick}>
          <ChartDefs
            totalLine={totalLine}
            layers={breakdownAreas}
            axisId={this.axisGradientId}
            pathsId={this.pathGradientId}
            areasId={this.areaGradientId}
            offset={highlighter && highlighter.x1}
            chartWidth={width}
          />
          <Axis {...xAxis} />
          <Axis {...yAxis} className="total-axis" title={totalLabel} />
          <Axis {...yBreakdownAxis} className="breakdown-axis" title={breakdownLabel} position="right" />
          <line {...xAxis.baseline} className="baseline" style={this.computeAxisStyle()} />
          <line {...yAxis.baseline} className="baseline" />
          <g>
            {map(breakdownAreas, (layer, i) =>
              <g key={layer.id}>
                {layer.line
                  && <path
                    className={classNames(
                      "path focus-enabled area-path",
                      {
                        visible: focusedChartLine && focusedChartLine === layer.id,
                        hidden: focusedChartLine && focusedChartLine !== layer.id,
                        "single-dot": data && data.length === 1,
                      },
                    )}
                    {...layer.line}
                    style={this.computeStyle(`${this.pathGradientId}-line-${i}`, layer.line.style.stroke, "stroke")}
                  />}
                <path
                  {...layer.area}
                  style={this.computeStyle(`${this.areaGradientId}-area-${i}`, layer.area.style.fill, "fill")}
                />
              </g>,
            )}
            <Dots
              {...{
                subscribe,
                unsubscribe,
                findDots: this.findBreakdownDots,
                focus: focusChartLine,
                blur: blurChartLine,
                focused: focusedChartLine,
              }}
            />
          </g>
          {totalLine
            && <g>
              <path
                className={classNames(
                  "path focus-enabled",
                  {
                    visible: focusedChartLine && focusedChartLine === totalLine.id,
                    hidden: focusedChartLine && focusedChartLine !== totalLine.id,
                    "single-dot": data && data.length === 1,
                  },
                )}
                {...totalLine}
                style={this.computeStyle(`${this.pathGradientId}-total`, totalLine.style.stroke, "stroke")}
              />
              <Dots
                {...{
                  subscribe,
                  unsubscribe,
                  findDots: this.findTotalDots,
                  focus: focusChartLine,
                  blur: blurChartLine,
                  focused: focusedChartLine,
                }}
              />
            </g>}
          {highlighter && <line className="highlighter" {...highlighter} />}
        </svg>
        <Tooltip {...{ legendRef: this.legendRef, subscribe, unsubscribe, findByOffset: this.findByOffset, position: "right" }}>
          {tooltip}
        </Tooltip>
      </div>
    }
  }
}

const mapStateToProps = (state, props) => ({
  totalLine: getTotalLine(props),
  breakdownAreas: getBreakdownAreas(props),
  xAxis: getXAxis(props),
  yAxis: getTotalYAxis(props),
  scaleX: getScaleX(props),
  totalScaleY: getTotalScaleY(props),
  breakdownScaleY: getBreakdownScaleY(props),
  breakdownItems: getBreakdownItems(props),
  totalItem: getTotalItem(props),
  findTotalByOffset: getFindTotalByOffset(props),
  findBreakdownByOffset: getFindBreakdownByOffset(props),
  yBreakdownAxis: getBreakdownYAxis(props),
  highlighter: getHighlighter(props),
  onlyLegendText: getOnlyLegendText(props),
})

export default tooltipContainer(resizable(connect(mapStateToProps)(MultiLineBreakdownChart)))
